import type { Theme } from '../../shared/themes';
import { clamp01, hsl, lerp, mixHex, noise, rgba, shade } from './palette';

/**
 * Everything above the horizon. Each world gets its own backdrop, drawn once
 * into an offscreen canvas per (theme, size) and then blitted with a parallax
 * offset — redrawing the skyline every frame is the easiest way to lose 60 fps
 * on a phone.
 */
export class SkyLayer {
  private w = 0;
  private h = 0;
  private themeId = '';
  private grad: CanvasGradient | null = null;
  private far: HTMLCanvasElement | null = null;
  private near: HTMLCanvasElement | null = null;
  private stars: { x: number; y: number; r: number; a: number }[] = [];

  resize(w: number, h: number, theme: Theme): void {
    if (this.w === w && this.h === h && this.themeId === theme.id) return;
    this.w = w;
    this.h = h;
    this.themeId = theme.id;
    this.grad = null;
    this.far = this.buildFar(theme);
    this.near = this.buildNear(theme);
    this.stars = [];
    if (theme.ambient === 'stars' || theme.id === 'antarctic') {
      for (let i = 0; i < 140; i++) {
        this.stars.push({
          x: noise(i * 3.1) * w,
          y: noise(i * 7.7) * h * 0.62,
          r: 0.5 + noise(i * 11.3) * 1.4,
          a: 0.25 + noise(i * 5.5) * 0.75,
        });
      }
    }
  }

  /**
   * @param horizon  screen y of the horizon line
   * @param sway     accumulated road curve, drives horizontal parallax
   * @param timeS    wall clock, for aurora and twinkle
   */
  draw(
    ctx: CanvasRenderingContext2D,
    theme: Theme,
    horizon: number,
    sway: number,
    timeS: number,
  ): void {
    const { w, h } = this;
    if (!this.grad) {
      const g = ctx.createLinearGradient(0, 0, 0, Math.max(1, horizon + h * 0.1));
      g.addColorStop(0, theme.sky.top);
      g.addColorStop(0.55, theme.sky.mid);
      g.addColorStop(1, theme.sky.horizon);
      this.grad = g;
    }
    ctx.fillStyle = this.grad;
    ctx.fillRect(0, 0, w, Math.max(0, horizon + 2));

    if (this.stars.length) this.drawStars(ctx, horizon, timeS);
    if (theme.id === 'antarctic') this.drawAurora(ctx, theme, horizon, timeS);
    if (theme.id === 'belt') this.drawNebula(ctx, theme, horizon, timeS);
    if (theme.id === 'chroma') this.drawChroma(ctx, horizon, timeS);

    if (theme.sun) {
      const sx = w * theme.sun.x + sway * -0.18;
      const sy = horizon - h * (1 - theme.sun.y) * 0.55;
      const r = Math.min(w, h) * theme.sun.r;
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 3.2);
      g.addColorStop(0, rgba(theme.sun.color, 0.95));
      g.addColorStop(0.24, rgba(theme.sun.color, 0.42));
      g.addColorStop(1, rgba(theme.sun.color, 0));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, horizon + 4);
      ctx.fillStyle = rgba(theme.sun.color, 0.9);
      ctx.beginPath();
      ctx.arc(sx, sy, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Two silhouette bands at different parallax rates give the ridge depth.
    if (this.far) ctx.drawImage(this.far, -sway * 0.1 - w * 0.1, horizon - this.far.height);
    if (this.near) ctx.drawImage(this.near, -sway * 0.24 - w * 0.1, horizon - this.near.height);

    // Haze where the world meets the sky, so distant road fades in rather than pops.
    const hz = ctx.createLinearGradient(0, horizon - h * 0.16, 0, horizon + 2);
    hz.addColorStop(0, rgba(theme.haze, 0));
    hz.addColorStop(1, rgba(theme.haze, 0.85));
    ctx.fillStyle = hz;
    ctx.fillRect(0, horizon - h * 0.16, w, h * 0.16 + 2);
  }

  private drawStars(ctx: CanvasRenderingContext2D, horizon: number, timeS: number): void {
    ctx.save();
    for (let i = 0; i < this.stars.length; i++) {
      const s = this.stars[i]!;
      if (s.y > horizon) continue;
      const tw = 0.65 + 0.35 * Math.sin(timeS * 1.7 + i * 2.3);
      ctx.globalAlpha = s.a * tw;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(s.x, s.y, s.r, s.r);
    }
    ctx.restore();
  }

  private drawAurora(ctx: CanvasRenderingContext2D, theme: Theme, horizon: number, t: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let band = 0; band < 3; band++) {
      const base = horizon - this.h * (0.18 + band * 0.12);
      const col = band === 0 ? theme.accent : band === 1 ? theme.accent2 : '#4de1ff';
      ctx.beginPath();
      ctx.moveTo(-20, base + 60);
      for (let x = -20; x <= this.w + 20; x += 14) {
        const k = x / this.w;
        const y =
          base +
          Math.sin(k * 5.2 + t * 0.5 + band * 1.7) * 26 +
          Math.sin(k * 11 + t * 0.9 + band) * 12;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(this.w + 20, base + 90);
      ctx.lineTo(-20, base + 90);
      ctx.closePath();
      const g = ctx.createLinearGradient(0, base - 40, 0, base + 90);
      g.addColorStop(0, rgba(col, 0));
      g.addColorStop(0.45, rgba(col, 0.3));
      g.addColorStop(1, rgba(col, 0));
      ctx.fillStyle = g;
      ctx.fill();
    }
    ctx.restore();
  }

  private drawNebula(ctx: CanvasRenderingContext2D, theme: Theme, horizon: number, t: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 3; i++) {
      const cx = this.w * (0.2 + i * 0.31) + Math.sin(t * 0.06 + i) * 18;
      const cy = horizon - this.h * (0.3 + i * 0.08);
      const r = this.w * (0.3 + i * 0.1);
      const col = i === 0 ? theme.accent2 : i === 1 ? '#4a2a9a' : theme.accent;
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      g.addColorStop(0, rgba(col, 0.16));
      g.addColorStop(1, rgba(col, 0));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, this.w, horizon);
    }
    // A ringed planet, because the asteroid belt should look like somewhere.
    const px = this.w * 0.74;
    const py = horizon - this.h * 0.36;
    const pr = Math.min(this.w, this.h) * 0.1;
    ctx.globalCompositeOperation = 'source-over';
    const pg = ctx.createRadialGradient(px - pr * 0.4, py - pr * 0.4, pr * 0.1, px, py, pr);
    pg.addColorStop(0, '#b98bff');
    pg.addColorStop(1, '#3a1f6b');
    ctx.fillStyle = pg;
    ctx.beginPath();
    ctx.arc(px, py, pr, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = rgba('#7cf7ff', 0.5);
    ctx.lineWidth = Math.max(1.5, pr * 0.08);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(-0.32);
    ctx.scale(1, 0.26);
    ctx.beginPath();
    ctx.arc(0, 0, pr * 1.7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    ctx.restore();
  }

  private drawChroma(ctx: CanvasRenderingContext2D, horizon: number, t: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 7; i++) {
      const y = horizon - this.h * (0.08 + i * 0.07);
      ctx.fillStyle = hsl(t * 24 + i * 42, 85, 58, 0.1);
      ctx.fillRect(0, y, this.w, this.h * 0.05);
    }
    ctx.restore();
  }

  // --- offscreen silhouette bands --------------------------------------

  private buildFar(theme: Theme): HTMLCanvasElement | null {
    return this.buildBand(theme, 0.34, mixHex(theme.sky.horizon, theme.haze, 0.62), 7.1, 0.55);
  }

  private buildNear(theme: Theme): HTMLCanvasElement | null {
    return this.buildBand(theme, 0.24, shade(theme.haze, -0.42), 3.3, 1);
  }

  private buildBand(
    theme: Theme,
    heightFrac: number,
    color: string,
    freq: number,
    detail: number,
  ): HTMLCanvasElement | null {
    if (this.w <= 0 || this.h <= 0) return null;
    const cw = Math.ceil(this.w * 1.25);
    const ch = Math.max(8, Math.ceil(this.h * heightFrac));
    const c = document.createElement('canvas');
    c.width = cw;
    c.height = ch;
    const g = c.getContext('2d');
    if (!g) return null;

    if (theme.id === 'metro') {
      this.drawSkyline(g, cw, ch, color, theme, detail);
      return c;
    }

    g.fillStyle = color;
    g.beginPath();
    g.moveTo(0, ch);
    for (let x = 0; x <= cw; x += 6) {
      const k = x / cw;
      const y =
        ch -
        ch *
          (0.32 +
            0.4 * Math.abs(Math.sin(k * freq + theme.id.length)) +
            0.16 * detail * Math.sin(k * freq * 3.7 + 1.3));
      g.lineTo(x, y);
    }
    g.lineTo(cw, ch);
    g.closePath();
    g.fill();

    if (theme.id === 'volcano') {
      // A cone with a lava crown, centred-ish, so the world has a landmark.
      const px = cw * 0.42;
      const peak = ch * 0.06;
      g.fillStyle = shade(color, -0.28);
      g.beginPath();
      g.moveTo(px - ch * 0.9, ch);
      g.lineTo(px, peak);
      g.lineTo(px + ch * 0.9, ch);
      g.closePath();
      g.fill();
      const lg = g.createLinearGradient(0, peak, 0, peak + ch * 0.4);
      lg.addColorStop(0, rgba('#ffca4d', 0.95));
      lg.addColorStop(1, rgba('#ff4d1a', 0));
      g.fillStyle = lg;
      g.beginPath();
      g.moveTo(px - ch * 0.12, peak + 2);
      g.lineTo(px + ch * 0.12, peak + 2);
      g.lineTo(px + ch * 0.3, peak + ch * 0.4);
      g.lineTo(px - ch * 0.3, peak + ch * 0.4);
      g.closePath();
      g.fill();
    }
    return c;
  }

  private drawSkyline(
    g: CanvasRenderingContext2D,
    cw: number,
    ch: number,
    color: string,
    theme: Theme,
    detail: number,
  ): void {
    let x = 0;
    let i = 0;
    while (x < cw) {
      const bw = 16 + noise(i * 3.7 + detail) * 54;
      const bh = ch * (0.28 + noise(i * 9.1 + detail) * 0.68);
      g.fillStyle = color;
      g.fillRect(x, ch - bh, bw, bh);
      // Lit windows. Sparse and warm, so the city reads as inhabited.
      const cols = Math.max(1, Math.floor(bw / 9));
      const rows = Math.max(1, Math.floor(bh / 12));
      for (let cx = 0; cx < cols; cx++) {
        for (let cy = 0; cy < rows; cy++) {
          const n = noise(i * 31 + cx * 7.3 + cy * 13.1);
          if (n < 0.62) continue;
          g.fillStyle = n > 0.93 ? rgba(theme.accent, 0.85) : rgba('#ffd98a', 0.5 + n * 0.4);
          g.fillRect(x + 3 + cx * 9, ch - bh + 5 + cy * 12, 3.5, 5);
        }
      }
      x += bw + 3 + noise(i * 5.3) * 10;
      i++;
    }
  }
}

/** Falling snow, drifting ash, sparks — one cheap particle field per world. */
export class AmbientLayer {
  private parts: { x: number; y: number; vx: number; vy: number; s: number; r: number }[] = [];
  private w = 0;
  private h = 0;

  resize(w: number, h: number, theme: Theme): void {
    this.w = w;
    this.h = h;
    const count = theme.ambient === 'none' ? 0 : theme.ambient === 'stars' ? 40 : 70;
    this.parts = [];
    for (let i = 0; i < count; i++) {
      this.parts.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: 0,
        vy: 0,
        s: 0.4 + Math.random() * 1.6,
        r: Math.random(),
      });
    }
  }

  draw(ctx: CanvasRenderingContext2D, theme: Theme, dt: number, speedRatio: number, sway: number): void {
    if (!this.parts.length) return;
    const drift = -sway * 0.02;
    ctx.save();
    for (const p of this.parts) {
      switch (theme.ambient) {
        case 'snow':
          p.vx = Math.sin(p.r * 9 + p.y * 0.01) * 22 + drift;
          p.vy = 40 + p.s * 55 + speedRatio * 190;
          ctx.fillStyle = rgba('#ffffff', 0.5 + p.r * 0.4);
          break;
        case 'ash':
          p.vx = Math.sin(p.r * 7 + p.y * 0.008) * 16 + drift;
          p.vy = 24 + p.s * 40 + speedRatio * 150;
          ctx.fillStyle = rgba(p.r > 0.82 ? theme.accent : '#8a7a70', 0.35 + p.r * 0.35);
          break;
        case 'leaves':
          p.vx = Math.sin(p.r * 5 + p.y * 0.012) * 40 + drift;
          p.vy = 60 + p.s * 70 + speedRatio * 220;
          ctx.fillStyle = rgba(p.r > 0.5 ? theme.accent : theme.accent2, 0.42);
          break;
        case 'confetti':
          p.vx = Math.sin(p.r * 8 + p.y * 0.02) * 55 + drift;
          p.vy = 70 + p.s * 90 + speedRatio * 240;
          ctx.fillStyle = hsl(p.r * 360, 88, 62, 0.7);
          break;
        case 'sparkle':
          p.vx = drift * 2;
          p.vy = -30 - p.s * 60 - speedRatio * 120;
          ctx.fillStyle = hsl(p.r * 360 + performance.now() * 0.05, 90, 70, 0.75);
          break;
        case 'stars':
        default:
          p.vx = drift * 3;
          p.vy = 10 + speedRatio * 420 * p.s;
          ctx.fillStyle = rgba('#ffffff', 0.35 + p.r * 0.5);
          break;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.y > this.h + 8) {
        p.y = -8;
        p.x = Math.random() * this.w;
      } else if (p.y < -8) {
        p.y = this.h + 8;
        p.x = Math.random() * this.w;
      }
      if (p.x < -8) p.x = this.w + 8;
      else if (p.x > this.w + 8) p.x = -8;

      const size = p.s * (theme.ambient === 'confetti' ? 3.2 : 2.1);
      ctx.fillRect(p.x, p.y, size, size * (theme.ambient === 'confetti' ? 1.8 : 1));
    }
    ctx.restore();
    void clamp01;
    void lerp;
  }
}
